import { useState, useEffect } from 'react';
import { Settings, Loader2, Save, Calendar, Clock, Plus, Trash2, RefreshCw, Shuffle, Ban } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { getSettings, updateSettings, reassignCalendarDates, getTeams } from '../../lib/api';
import { toast } from 'sonner';

export const AdminImpostazioniPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  
  // Match days: array of { date: "2026-05-26", times: ["20:30", "21:30", "22:30"] }
  const [matchDays, setMatchDays] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamUnavailability, setTeamUnavailability] = useState({});
  const [unavailTeam, setUnavailTeam] = useState('');
  const [unavailDate, setUnavailDate] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, teamsRes] = await Promise.all([getSettings(), getTeams()]);
        const data = settingsRes.data;
        setSettings(data);
        setTeams(teamsRes.data);
        setTeamUnavailability(data.team_unavailability || {});
        
        if (data.match_schedule && data.match_schedule.length > 0) {
          setMatchDays(data.match_schedule);
        } else {
          // Default tournament schedule
          setMatchDays([
            { date: '2026-05-26', times: ['20:30', '21:30', '22:30'], label: 'Girone 1' },
            { date: '2026-05-28', times: ['20:30', '21:30', '22:30'], label: 'Girone 2' },
            { date: '2026-06-04', times: ['20:30', '21:30', '22:30'], label: 'Girone 3' },
            { date: '2026-06-06', times: ['20:30', '21:30', '22:30'], label: 'Girone 4' },
            { date: '2026-06-09', times: ['20:30', '21:30', '22:30'], label: 'Girone 5' },
            { date: '2026-06-11', times: ['20:30', '21:30', '22:30'], label: 'Girone 6' },
            { date: '2026-06-13', times: ['20:30', '21:30', '22:30'], label: 'Recupero' },
            { date: '2026-06-16', times: ['20:30', '21:30', '22:30'], label: 'Girone 7' },
            { date: '2026-06-18', times: ['20:30', '21:30', '22:30'], label: 'Girone 8' },
            { date: '2026-06-20', times: ['20:30', '21:30', '22:30'], label: '1° Quarto Andata' },
            { date: '2026-06-23', times: ['20:30', '21:30', '22:30'], label: '1° Quarto Ritorno' },
            { date: '2026-06-25', times: ['20:30', '21:30', '22:30'], label: 'Recupero' },
            { date: '2026-06-27', times: ['20:30', '21:30', '22:30'], label: '2° Quarto Andata' },
            { date: '2026-06-30', times: ['20:30', '21:30', '22:30'], label: '2° Quarto Ritorno' },
            { date: '2026-07-02', times: ['20:30', '21:30', '22:30'], label: 'Semifinale Andata' },
            { date: '2026-07-04', times: ['20:30', '21:30', '22:30'], label: 'Semifinale Ritorno' },
            { date: '2026-07-07', times: ['20:30', '21:30', '22:30'], label: 'Finale' },
            { date: '2026-07-09', times: ['20:30', '21:30', '22:30'], label: 'Recupero' },
          ]);
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Errore nel caricamento delle impostazioni');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({
        yellow_card_fine: parseFloat(settings.yellow_card_fine) || 10,
        red_card_fine: parseFloat(settings.red_card_fine) || 25,
        match_schedule: matchDays.filter(d => d.date && d.times.length > 0),
        team_unavailability: teamUnavailability
      });
      toast.success('Impostazioni salvate');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const addMatchDay = () => {
    if (!newDate) {
      toast.error('Seleziona una data');
      return;
    }
    if (matchDays.find(d => d.date === newDate)) {
      toast.error('Data già presente');
      return;
    }
    setMatchDays([...matchDays, { date: newDate, times: ['20:30'] }].sort((a, b) => a.date.localeCompare(b.date)));
    setNewDate('');
  };

  const removeMatchDay = (date) => {
    setMatchDays(matchDays.filter(d => d.date !== date));
  };

  const addTimeToDay = (date) => {
    setMatchDays(matchDays.map(d => {
      if (d.date === date) {
        return { ...d, times: [...d.times, ''] };
      }
      return d;
    }));
  };

  const updateTime = (date, timeIndex, value) => {
    setMatchDays(matchDays.map(d => {
      if (d.date === date) {
        const newTimes = [...d.times];
        newTimes[timeIndex] = value;
        return { ...d, times: newTimes };
      }
      return d;
    }));
  };

  const removeTime = (date, timeIndex) => {
    setMatchDays(matchDays.map(d => {
      if (d.date === date) {
        return { ...d, times: d.times.filter((_, i) => i !== timeIndex) };
      }
      return d;
    }));
  };

  const updateLabel = (date, value) => {
    setMatchDays(matchDays.map(d => {
      if (d.date === date) {
        return { ...d, label: value };
      }
      return d;
    }));
  };

  const handleReassignDates = async () => {
    setReassigning(true);
    try {
      await updateSettings({
        yellow_card_fine: parseFloat(settings.yellow_card_fine) || 10,
        red_card_fine: parseFloat(settings.red_card_fine) || 25,
        match_schedule: matchDays.filter(d => d.date && d.times.length > 0),
        team_unavailability: teamUnavailability
      });
      const res = await reassignCalendarDates();
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento date');
    } finally {
      setReassigning(false);
    }
  };

  const addUnavailability = () => {
    if (!unavailTeam || !unavailDate) {
      toast.error('Seleziona squadra e data');
      return;
    }
    const current = teamUnavailability[unavailTeam] || [];
    if (current.includes(unavailDate)) {
      toast.error('Indisponibilita gia inserita');
      return;
    }
    setTeamUnavailability({
      ...teamUnavailability,
      [unavailTeam]: [...current, unavailDate].sort()
    });
    setUnavailDate('');
    toast.success('Indisponibilità aggiunta · Premi "Aggiorna Date Partite" per applicarla al calendario');
  };

  const removeUnavailability = (teamId, date) => {
    const current = teamUnavailability[teamId] || [];
    const updated = current.filter(d => d !== date);
    if (updated.length === 0) {
      const newMap = { ...teamUnavailability };
      delete newMap[teamId];
      setTeamUnavailability(newMap);
    } else {
      setTeamUnavailability({ ...teamUnavailability, [teamId]: updated });
    }
  };

  const getTeamName = (id) => teams.find(t => t.id === id)?.name || id;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00Z');
    return date.toLocaleDateString('it-IT', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-impostazioni">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1">IMPOSTAZIONI</h1>
          <p className="text-slate-400 text-sm">Configura i parametri del torneo</p>
        </div>

        {/* Calendar Settings */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Calendario Partite
            </CardTitle>
            <CardDescription>
              Gestisci le date e gli orari delle partite. Il calendario verrà generato in base a queste impostazioni.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Add New Date */}
            <div className="flex gap-2">
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="bg-slate-950 border-slate-800 flex-1"
                min="2026-05-01"
              />
              <Button
                onClick={addMatchDay}
                className="bg-primary text-slate-950 hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi Data
              </Button>
            </div>

            {/* Match Days List */}
            <div className="space-y-4">
              {matchDays.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Nessuna data configurata</p>
              ) : (
                matchDays.map((day) => (
                  <div key={day.date} className={`p-4 bg-slate-950 rounded-lg border ${day.label?.toLowerCase().trim() === 'recupero' ? 'border-yellow-600/40' : 'border-slate-800'}`}>
                    <div className="flex items-center justify-between mb-3 gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold capitalize text-sm md:text-base">{formatDate(day.date)}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Input
                            type="text"
                            value={day.label || ''}
                            onChange={(e) => updateLabel(day.date, e.target.value)}
                            placeholder="Es: Girone 1, Recupero..."
                            className="bg-slate-900 border-slate-700 text-xs h-7 max-w-[200px] min-w-0"
                          />
                          {day.label?.toLowerCase().trim() === 'recupero' && (
                            <span className="text-[10px] px-2 py-0.5 bg-yellow-600/20 text-yellow-400 rounded-full whitespace-nowrap">
                              Non si gioca
                            </span>
                          )}
                        </div>
                        {day.label?.toLowerCase().trim() !== 'recupero' && (
                          <p className="text-xs text-slate-500 mt-1">{day.times.filter(t => t).length} partite</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMatchDay(day.date)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Orari partite
                      </Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {day.times.map((time, timeIndex) => (
                          <div key={timeIndex} className="flex gap-1 min-w-0">
                            <Input
                              type="time"
                              value={time}
                              onChange={(e) => updateTime(day.date, timeIndex, e.target.value)}
                              className="bg-slate-900 border-slate-700 text-sm h-9 min-w-0 flex-1"
                            />
                            {day.times.length > 1 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTime(day.date, timeIndex)}
                                className="text-red-400 hover:text-red-300 px-2 h-9"
                              >
                                ×
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addTimeToDay(day.date)}
                          className="border-dashed border-slate-700 text-slate-400 h-9"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Orario
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Totale slot disponibili: {matchDays.filter(d => d.label?.toLowerCase().trim() !== 'recupero').reduce((acc, d) => acc + d.times.filter(t => t).length, 0)} partite
                {matchDays.filter(d => d.label?.toLowerCase().trim() === 'recupero').length > 0 && (
                  <span className="text-yellow-400 ml-2">
                    ({matchDays.filter(d => d.label?.toLowerCase().trim() === 'recupero').length} giorni di recupero esclusi)
                  </span>
                )}
              </p>
              <Button
                onClick={handleReassignDates}
                disabled={reassigning}
                variant="outline"
                size="sm"
                className="border-primary/50 text-primary hover:bg-primary/10"
                data-testid="reassign-dates-btn"
              >
                {reassigning ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1" />
                )}
                Aggiorna Date Partite
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Team Unavailability - applies on calendar reassignment */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-400" />
              Calendario - Indisponibilità Squadre
            </CardTitle>
            <CardDescription>
              Imposta le date in cui una squadra non può giocare. Premi <strong>"Aggiorna Date Partite"</strong> sopra per applicare le indisponibilità al calendario esistente (i sorteggi non vengono toccati).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={unavailTeam} onValueChange={setUnavailTeam}>
                <SelectTrigger className="bg-slate-950 border-slate-800 flex-1" data-testid="unavail-team-select">
                  <SelectValue placeholder="Seleziona squadra" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={unavailDate}
                onChange={(e) => setUnavailDate(e.target.value)}
                className="bg-slate-950 border-slate-800 w-[180px]"
                data-testid="unavail-date-input"
              />
              <Button
                onClick={addUnavailability}
                size="sm"
                className="bg-primary text-slate-950"
                data-testid="add-unavail-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi
              </Button>
            </div>

            {Object.keys(teamUnavailability).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(teamUnavailability).map(([teamId, dates]) => (
                  <div key={teamId} className="p-3 bg-slate-950 rounded-lg border border-slate-800">
                    <p className="font-semibold text-sm mb-2 flex items-center gap-2">
                      <Ban className="w-3.5 h-3.5 text-red-400" />
                      {getTeamName(teamId)}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {dates.map(date => (
                        <span key={date} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-red-500/10 text-red-400 rounded-lg border border-red-500/20">
                          {new Date(date + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                          <button onClick={() => removeUnavailability(teamId, date)} className="hover:text-red-300">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500">Nessuna indisponibilita configurata. Tutte le squadre possono giocare in qualsiasi data.</p>
            )}
          </CardContent>
        </Card>

        {/* Sanctions Settings */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-gold" />
              Multe Sanzioni
            </CardTitle>
            <CardDescription>
              Imposta i valori predefiniti per le multe di ammonizioni ed espulsioni
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className="yellow-card" />
                  Ammonizione (€)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings?.yellow_card_fine || 10}
                  onChange={(e) => setSettings({ ...settings, yellow_card_fine: e.target.value })}
                  className="bg-slate-950 border-slate-800"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className="red-card" />
                  Espulsione (€)
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={settings?.red_card_fine || 25}
                  onChange={(e) => setSettings({ ...settings, red_card_fine: e.target.value })}
                  className="bg-slate-950 border-slate-800"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tournament Info */}
        <Card className="bg-slate-900 border-slate-800 mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Informazioni Torneo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b border-slate-800">
              <span className="text-slate-400">Stato:</span>
              <span className="capitalize">{settings?.current_phase || 'Registrazione'}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-800">
              <span className="text-slate-400">Sorteggio completato:</span>
              <span>{settings?.draw_completed ? 'Sì' : 'No'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-400">Data inizio:</span>
              <span>26 Maggio 2026</span>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-primary text-slate-950 hover:bg-primary/90"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Salva Impostazioni
        </Button>
      </div>
    </div>
  );
};

export default AdminImpostazioniPage;
